import $ from 'jquery';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import isEmpty from 'lodash/isEmpty';

import ErrorBoundary from 'zoomforth_component_lib/error_boundary.jsx';

import SectionVideoBackground from './section_video_background.jsx';

import { SECTION_WIDTH_CHANGE_EVENT } from 'zoomforth_codesbane_viewer/viewer/managers/subpage_resize_event_manager.jsx';

import { SECTION_TYPE_TILES } from 'zoomforth_codesbane_viewer/const/sections.js';

import SectionManagerForViewerWrapper from './section_manager_for_viewer_wrapper.jsx';

export const calculateContainerWidth = (container, isUsingV1Styles=false) => {
  // codesbane sections should not worry about pixel snapping
  if (!isUsingV1Styles) {
    return $(container).width();
  }

  // @NOTE:
  // although every section isn't a grid, we use the legacy logic to size all
  // sections accordingly. if we were to use the containerWidth alone codesbane
  // sections would be slightly larger than before making text larger.
  const containerWidth = $(container).width();

  // since it was impractical to resize a grid fluidly, 
  // it snaps to a point where it resizes every 60 px.
  const WIDTH_INCREMENT_PX = 60; 

  // these base pixels accomodate the requirement in the
  // editor iframe that there be a >=1px margin between tiles
  const BASE_EXTRA_WIDTH = 20;

  const widthIncrementIndex = parseInt(
    (containerWidth - BASE_EXTRA_WIDTH) /
    WIDTH_INCREMENT_PX
  );

  return WIDTH_INCREMENT_PX * widthIncrementIndex;
};

class SectionManagerForViewer extends Component {

  constructor() {
    super();

    this.state = {
      isInitialRender: true,
      containerWidth: undefined
    };

    // we need something we can use even if the section does not have an ID yet
    this.resizeKey = 'section' + Math.floor(Math.random()*1000000);
  }

  componentDidMount() {
    this.setState({
      isInitialRender: false,
      containerWidth: calculateContainerWidth(
        this.containerRef,
        this.props.isUsingV1Styles
      )
    });

    $(window).on(`${SECTION_WIDTH_CHANGE_EVENT}.${this.resizeKey}`, () => {
      this.setState({
        containerWidth: calculateContainerWidth(
          this.containerRef,
          this.props.isUsingV1Styles
        )
      });
    });
  }

  componentWillUnmount() {
    $(window).off(`${SECTION_WIDTH_CHANGE_EVENT}.${this.resizeKey}`);
  }

  render() {
    const { isInitialRender, containerWidth } = this.state;

    const {
      backgroundStyle,
      backgroundClasses,
      backgroundVideoParams,
      cssClassesList,
      section,
      isGlassOverlayEnabled,
      isCustomOverlayEnabled,
      overlayOpacity,
      overlayColor,
      sectionProps,
      SectionRenderer,
      sectionWrapperClassname,
      sectionMarginStyle,
      sectionPaddingStyle,
      onSectionRefReceive,
      isChoosingTarget,
      children
    } = this.props;

    if (isInitialRender) {
      return (
        <div
          className={classNames('section-wrapper', sectionWrapperClassname, {
            'editing-target-for-internal-link': isChoosingTarget
          })}
          ref={onSectionRefReceive}
        >
          <div
            // this is a backwards compatibility concession, see 
            // the description in the main rendered content below
            className="outer-tiles-wrapper"
            ref={(ref)=>{ this.containerRef = ref; }}
          />
        </div>
      );
    }

    // theoretically this will never happen but it's a nice safety measure
    if (containerWidth === undefined) {
      throw new Error('containerWidth must not be undefined -- it should have been set in the preload measurement phase');
    }

    const { sectionFontSize, ...restSectionProps } = sectionProps || {};

    // we used to calculate sectionWidth and have it be smaller than containerWidth, 
    // but we do not have to do that anymore since our editor has improved.
    // now all sections should be passed "sectionWidth" and have that 
    // be the smae as the size of the container.
    const sectionWidth = containerWidth;

    // Grid sections are the only section which differs for where the padding
    // is applied. In that case we apply it to the `.tiles` inner div due to
    // backwards compatibility concerns. This is lame but handled.
    var sectionWrapperStyle = { ...backgroundStyle, ...sectionMarginStyle };
    if (section && section.type !== SECTION_TYPE_TILES) {
      sectionWrapperStyle = { ...sectionWrapperStyle, ...sectionPaddingStyle };
    }

    restSectionProps.sectionPaddingStyle = sectionPaddingStyle;

    const handleSectionClick = (event) => {
      if (this.props.onClick) {
        this.props.onClick(event);
      }
    };

    return (
      <SectionManagerForViewerWrapper
        tellRef={onSectionRefReceive}
        id={section ? `section-id-${section.id}` : null}
        className={classNames('section-wrapper', sectionWrapperClassname, cssClassesList, backgroundClasses, {
          'editing-target-for-internal-link': isChoosingTarget
        })}
        style={sectionWrapperStyle}
        onClick={handleSectionClick}
      >

        { !isEmpty(backgroundVideoParams) && <SectionVideoBackground {...backgroundVideoParams} /> }

        {
          (isGlassOverlayEnabled || isCustomOverlayEnabled) &&
            <div
              className="section-custom-overlay"
              style={{
                opacity: overlayOpacity || 1,
                backgroundColor: isCustomOverlayEnabled ? overlayColor || '#000000' : 'inherit'
              }}
            />
        }     

        <div 
          ref={(ref)=>{ this.containerRef = ref; }}
          // this is a backwards compatibility concession:
          // although these sections can be of any type,
          // this inner wrapper should ALWAYS have a class name of outer-tiles-wrapper
          // since themes were targeting it as such and we want any rules
          // that applied to a grid to apply similarly to other sections
          className="outer-tiles-wrapper"
          style={{
            fontSize: sectionFontSize
          }}
        >

          <ErrorBoundary>
            <SectionRenderer
              section={section}
              sectionWidth={sectionWidth}
              {...restSectionProps}
            />
          </ErrorBoundary>

        </div>


        {children}

      </SectionManagerForViewerWrapper>
    );
  }

}

SectionManagerForViewer.propTypes = {
  section: PropTypes.object,
  sectionWrapperClassname: PropTypes.string.isRequired,
  SectionRenderer: PropTypes.func.isRequired,
  onSectionRefReceive: PropTypes.func,
  isChoosingTarget: PropTypes.bool
};

export default SectionManagerForViewer;
